import type { DeviceAction, DeviceActionUI } from 'src/types/DeviceActionUI';

import type { GroupActionUI } from 'src/types/GroupActionUI';
import type { NotificationActionUI } from 'src/types/NotificationActionUI';
import type { SceneActionUI } from 'src/types/SceneActionUI';
import { _init_notifications } from 'src/components/Scenes/helpers/init_notification_actions';
import {
	extractGuiDeviceId,
	convertSceneCommandToUiProperty,
} from 'src/components/Scenes/helpers/utilities_scenes_to_ui';
import { shellyId16 } from 'src/helpers/interface_helpers';
import { get } from 'svelte/store';
import { scenes } from 'src/stores/lists/scenes';
import type { DeviceID } from 'src/types/DeviceID';
import type { DeviceActionScript } from 'src/types/ScenesActionsTypes';
import { getDelayedSceneAction } from 'src/components/Scenes/helpers/get_delayed_scene_action';

interface ParsedAction {
	channel: number;
	actions: DeviceAction[];
}

// -------- actions interpetator functions -------
// translate actions helpers
const extractActionAndChannelFromSceneCommand = (sceneCommand, set): ParsedAction => {
	let doArr = [];
	let channel = 0;
	if (sceneCommand === 'setup' && typeof set === 'object') {
		for (const key in set) {
			if (key === '_isdata') continue;
			// key is a command that need to translate and check for channel
			let t = convertSceneCommandToUiProperty(key, set[key]);
			// to avoid preset again to 0 index if we have mode + r2 for example
			// in fact we must never come in this case, but who knows
			channel = channel > t.channel ? channel : t.channel;

			// Convert diffuser intensity actions to cury_intensity with slot
			if (t.name === 'intensity_left') {
				t.name = 'cury_intensity';
				t.slot = 'left';
			} else if (t.name === 'intensity_right') {
				t.name = 'cury_intensity';
				t.slot = 'right';
			}

			doArr.push({
				name: t.name,
				value: t.value,
				...(t?.slot && { slot: t.slot }),
				// [t.command] : set[key]
			});
		}
	} else {
		let act = convertSceneCommandToUiProperty(sceneCommand, set);
		channel = channel > act.channel ? channel : act.channel;

		// Convert diffuser intensity actions to cury_intensity with slot
		if (act.name === 'intensity_left') {
			act.name = 'cury_intensity';
			act.slot = 'left';
		} else if (act.name === 'intensity_right') {
			act.name = 'cury_intensity';
			act.slot = 'right';
		}

		doArr.push({
			name: act.name,
			value: act.value,
			...(act?.add_info_ind && { addOnInd: act.add_info_ind }),
			...(act?.input_index && { index: act.input_index }),
			...(act?.slot && { slot: act.slot }),
			// Camera StartRecording carries an optional duration that must survive edit round-trips.
			...(act?.duration != null && { duration: act.duration }),
		});
	}
	return { channel, actions: doArr };
};

/** Unwraps delayed actions while preserving combined multi-command blk payloads. */
export const unwrapSceneActionForInterpretation = (element: any) => {
	const inner = getDelayedSceneAction(element);
	if (inner !== undefined && element.wait !== undefined) {
		return {
			...inner,
			delay: element.wait,
			units: element.units,
		};
	}

	return element;
};

const attachActionTiming = (translated: DeviceActionUI, action: DeviceActionScript | any) => {
	if (
		typeof translated !== 'undefined' &&
		typeof action.units !== 'undefined' &&
		typeof action.delay !== 'undefined'
	) {
		translated.delay = {
			units: action.units === 'minutes' ? 'min' : 'sec',
			value: action.delay,
		};
	}

	if (
		typeof translated !== 'undefined' &&
		typeof action.turntimer_unit !== 'undefined' &&
		typeof action.turntimer !== 'undefined'
	) {
		translated.timer = {
			units: action.turntimer_unit,
			value: action.turntimer,
		};
	}

	return translated;
};

// main part
//@ToDo: Revise types usage. As of implementing VC in Scenes there were missing types
export const deviceActionInterpretator = (action: DeviceActionScript | any): DeviceActionUI => {
	let translated = undefined;
	const { r, set } = action;
	if (r !== undefined) {
		let [reason = 'dev', decimalId = undefined, sceneAction = ''] = r.split(':');
		let { channel = 0, actions = [] }: ParsedAction = extractActionAndChannelFromSceneCommand(
			sceneAction,
			set,
		);

		let gui_device: DeviceID;

		// For XT devices, _gui_device_id contains the full device ID string
		// Use it directly instead of trying to convert decimalId
		if (action?._gui_device_id) {
			gui_device = extractGuiDeviceId(action?._gui_device_id);
			// Preserve the channel from the parsed action (for virtual components)
			if (channel > 0) {
				gui_device.channel = channel;
			}
		} else {
			// For regular devices, convert decimal ID to hex
			let hexDevice = shellyId16(decimalId);
			gui_device = {
				id: hexDevice,
				channel: channel,
			};
		}
		translated = {
			device: gui_device,
			actions,
		};
	}

	return attachActionTiming(translated, action);
};

export const notificationActionInterpretator = (act, msg_type = 'push'): NotificationActionUI => {
	let obj = undefined;
	let msg = '';
	switch (act.notify) {
		case 'push_notification':
			msg = act.msg || 'My scene triggered!';
			obj = {
				name: `${msg_type} notification`,
				msg: msg,
				msg_type,
				type: act.notify,
			};
			break;

		case 'event_log':
			obj = {
				name: 'Add to event log',
				msg: '',
				msg_type: 'event_log',
				type: act.notify,
			};
			break;

		case 'alexa_notif':
			obj = {
				name: 'Notify Alexa',
				msg: '',
				msg_type: act?.type === 'premium' ? 'alexa_notif_premium' : 'alexa_notif',
				type: act.notify,
			};
			break;
		case 'mail_notification':
			obj = {
				name: 'Email notificationl',
				msg: act.msg || 'My scene triggered!',
				msg_type: 'email',
				type: act.notify,
			};
			break;
		default:
			return undefined;
	}

	obj.id = _init_notifications.findIndex((el) => el.props.id === act.notify);
	if (
		typeof obj !== 'undefined' &&
		typeof act.units !== 'undefined' &&
		typeof act.delay !== 'undefined'
	) {
		obj.delay = {
			units: act.units === 'minutes' ? 'min' : 'sec',
			value: act.delay,
		};
	}

	/**
	 * FE-1682: surface the sub-unit recipient list for push/mail notifications
	 * when stored on the action.
	 *
	 * - Legacy actions without `toSub` stay `undefined`, which the wizard reads
	 *   as "all current sub-units" until the user saves.
	 * - Numbers are coerced defensively so a malformed BE payload does not
	 *   crash the UI.
	 */
	if (typeof obj !== 'undefined' && Array.isArray(act.toSub)) {
		obj.toSub = act.toSub
			.map((value: unknown) => Number(value))
			.filter((value: number): value is number => Number.isFinite(value));
	}

	// FE-1682: per-action owner opt-out flag.
	if (typeof obj !== 'undefined' && act.pushSkipUnit === true) {
		obj.pushSkipUnit = true;
	}

	return obj;
};

export const sceneActionInterpretator = (action) => {
	const obj = {
		scene_id: action.trigger,
		option: action.skipif ? 'without_if_evaluation' : 'with_if_evaluation',
	} as SceneActionUI;

	if (typeof action.units !== 'undefined' && typeof action.delay !== 'undefined') {
		obj.delay = {
			units: action.units === 'minutes' ? 'min' : 'sec',
			value: action.delay,
		};
	}

	return obj;
};

export const enableDisableSceneActionInterpretator = (action) => {
	const idOwner = action.hasOwnProperty('r') ? 'r' : 'rr';
	const id = action[idOwner].split(':')[1];
	const _scenes = get(scenes);
	let action_type =
		_scenes[id]?._gui_function && _scenes[id]?._gui_function === 'alarm' ? 'alarm' : 'scene';

	const mapActions = {
		true: `action_enable_${action_type}`,
		false: `action_disable_${action_type}`,
		toggle: `action_toggle_${action_type}`,
	};

	const obj = {
		scene_id: id.toString(),
		option: mapActions[action.set.toString()],
	} as SceneActionUI;

	if (typeof action.units !== 'undefined' && typeof action.delay !== 'undefined') {
		obj.delay = {
			units: action.units === 'minutes' ? 'min' : 'sec',
			value: action.delay,
		};
	}

	return obj;
};

export const groupActionInterpretator = (action): GroupActionUI | undefined => {
	const name =
		typeof action.turn !== 'undefined'
			? 'turn'
			: typeof action.direction !== 'undefined'
				? 'direction'
				: typeof action.position !== 'undefined'
					? 'position'
					: 'turn';

	const obj: any = {
		_type: 'group',
		group_id: action.grpctrl,
		actions: [
			{
				name: name,
				value: action[name],
			},
		],
	};

	if (typeof action.units !== 'undefined' && typeof action.delay !== 'undefined') {
		obj.delay = {
			units: action.units === 'minutes' ? 'min' : 'sec',
			value: action.delay,
		};
	}

	return obj;
};

export const actionInterpretator = (
	purpose,
	actionCopy,
	msg_type = 'push',
): NotificationActionUI | DeviceActionUI | SceneActionUI | GroupActionUI | undefined => {
	switch (purpose) {
		case 'action':
			return deviceActionInterpretator(actionCopy);

		case 'notification':
			return notificationActionInterpretator(actionCopy, msg_type);

		case 'group_action':
			return groupActionInterpretator(actionCopy);

		case 'activate_scene':
			return sceneActionInterpretator(actionCopy);

		case 'enable_disable_scene':
			return enableDisableSceneActionInterpretator(actionCopy);

		default:
			if (typeof actionCopy?.trigger === 'string') {
				return sceneActionInterpretator(actionCopy);
			}
			// Making this a log so clients dont get scared.
			console.log('WRONG ACTION PURPOSE');
			return undefined;
	}
};

// ----- end actions interpretator -----
